import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useUserPreferences } from '../contexts/UserPreferencesContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { useProfile, type AccountType } from '../contexts/ProfileContext';
import { useBugReport } from '../contexts/BugReportContext';
import type { ThemePreference, AppMode } from '../contexts/UserPreferencesContext';
import { useEffect, useState } from 'react';
import { Sun, Moon, ChevronDown, User, LogOut, Menu, Eye } from 'lucide-react';

import { toastError } from '../lib/toast-error';
import { useMediaQuery } from '../hooks/useIsMobile';

import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from './ui';
import CreditsBadge from './credits/CreditsBadge';
import NavDrawer, { type NavItem } from './NavDrawer';

import styles from './Navbar.module.css';
import { LogoImage } from './ui/LogoImage';

/** Shows the icon for the theme you'll switch TO (Sun = switch to light, Moon = switch to dark) */
function ThemeToggleIcon({ currentTheme }: { currentTheme: ThemePreference }) {
  return currentTheme === 'light' ? <Moon size={20} aria-hidden /> : <Sun size={20} aria-hidden />;
}

export const NAV_ITEMS_ESTUDANTE: NavItem[] = [
  { label: 'Dashboard', path: '/dashboard' },
  { label: 'Simulados', path: '/catalogo' },
  { label: 'Histórico', path: '/historico' },
  // Desativado temporariamente (ago/2026): a feature continua de pé em
  // `/correcoes`, só saiu do menu. Para reativar, descomente a linha.
  // { label: 'Corrigir minha prova', path: '/correcoes' },
  { label: 'Planos', path: '/assinatura' },
  { label: 'Dar feedback', path: '/feedback' },
  { label: 'Reportar bug', path: '#bug', action: 'bug-report' },
  { label: 'Conta', path: '/conta' },
];

const NAV_ITEMS_PROFESSOR: NavItem[] = [
  { label: 'Início', path: '/dashboard' },
  { label: 'Turmas', path: '/professor/turmas' },
  { label: 'Correções', path: '/professor/correcoes' },
  { label: 'Nova Correção', path: '/professor/correcoes/nova' },
  { label: 'Planos', path: '/assinatura' },
  { label: 'Reportar bug', path: '#bug', action: 'bug-report' },
  { label: 'Conta', path: '/conta' },
];

const NAV_ITEMS_EMPRESARIAL: NavItem[] = [
  { label: 'Dashboard', path: '/empresarial' },
  { label: 'Nova Correção', path: '/empresarial/correcoes/nova' },
  { label: 'Correções', path: '/empresarial/correcoes' },
  { label: 'Planos', path: '/assinatura' },
  { label: 'Reportar bug', path: '#bug', action: 'bug-report' },
  { label: 'Conta', path: '/conta' },
];

const MODE_OPTIONS: { value: AppMode; label: string }[] = [
  { value: 'estudante', label: 'Estudante' },
  { value: 'professor', label: 'Professor' },
];

const PUBLIC_NAV_ANCHORS: { label: string; href: string }[] = [
  { label: 'Simulados', href: '#catalogo' },
  { label: 'Como funciona', href: '#como-funciona' },
  { label: 'Planos', href: '#planos' },
  { label: 'FAQ', href: '#faq' },
];

/**
 * O badge de saldo só existe para conta `business`. Crédito é a moeda do B2B;
 * o aluno B2C assina o plano único (correção e chat ilimitados) e não tem saldo
 * que signifique algo.
 */
export function shouldShowCreditsBadge(accountType: AccountType): boolean {
  return accountType === 'business';
}

function getNavItemsForMode(mode: AppMode): NavItem[] {
  if (mode === 'professor') return NAV_ITEMS_PROFESSOR;
  if (mode === 'empresarial') return NAV_ITEMS_EMPRESARIAL;
  return NAV_ITEMS_ESTUDANTE;
}

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { preferences, setTheme, setAppMode } = useUserPreferences();
  const { startImpersonation, isImpersonating } = useImpersonation();
  const { data: userData, accountType } = useProfile();
  const { open: openBugReport } = useBugReport();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [impersonateUid, setImpersonateUid] = useState('');
  const [impersonateLoading, setImpersonateLoading] = useState(false);
  const isMobileNav = useMediaQuery('(max-width: 1024px)');

  // Close the drawer if the viewport grows past the mobile breakpoint.
  useEffect(() => {
    if (!isMobileNav && mobileMenuOpen) setMobileMenuOpen(false);
  }, [isMobileNav, mobileMenuOpen]);

  const toggleTheme = () => {
    setTheme(preferences.theme === 'light' ? 'dark' : 'light');
  };

  const handleImpersonate = async () => {
    const uid = impersonateUid.trim();
    if (!uid) return;
    setImpersonateLoading(true);
    try {
      await startImpersonation(uid);
      setMobileMenuOpen(false);
    } catch (err: any) {
      toastError(err.message || 'Erro ao impersonar usuário', {
        errorMessage: err?.message,
        errorRequestId: err?.requestId,
      });
    } finally {
      setImpersonateLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const getStatusClass = (status: string) => {
    if (status === 'active') return styles.statusActive;
    if (status === 'canceled') return styles.statusCanceled;
    return styles.statusOther;
  };

  const baseItems = getNavItemsForMode(preferences.appMode);
  const navItems: NavItem[] = [
    ...baseItems,
    ...(preferences.appMode === 'estudante' && accountType === 'business'
      ? [{ label: 'Professor', path: '/professor/turmas' }]
      : []),
    ...(userData?.roles?.admin ? [{ label: 'Admin', path: '/admin' }] : []),
  ];

  const handleModeChange = (mode: AppMode) => {
    // Redirect empresarial users to professor (merged modules)
    const effectiveMode = mode === 'empresarial' ? 'professor' : mode;
    setAppMode(effectiveMode);
    navigate('/dashboard');
  };

  const isActive = (path: string) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    if (path === '/empresarial')
      return location.pathname === '/empresarial' && !location.pathname.startsWith('/empresarial/');
    return location.pathname.startsWith(path);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const handleNav = (path: string) => {
    if (path === '/professor/turmas' && preferences.appMode !== 'professor') {
      setAppMode('professor');
    }
    navigate(path);
    closeMobileMenu();
  };

  const handleNavItem = (item: NavItem) => {
    if (item.action === 'bug-report') {
      openBugReport();
      closeMobileMenu();
      return;
    }
    handleNav(item.path);
  };

  const renderImpersonateSection = () => (
    <div className={styles.impersonateSection} onKeyDown={(e) => e.stopPropagation()}>
      <div className={styles.impersonateLabel}>
        <Eye size={14} aria-hidden />
        Visualizar como
      </div>
      <div className={styles.impersonateInput}>
        <input
          type="text"
          placeholder="UID do usuário"
          value={impersonateUid}
          onChange={(e) => setImpersonateUid(e.target.value)}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter') handleImpersonate();
          }}
          className={styles.impersonateField}
          aria-label="UID do usuário para impersonar"
        />
        <button
          type="button"
          onClick={handleImpersonate}
          disabled={impersonateLoading || !impersonateUid.trim()}
          className={styles.impersonateBtn}
        >
          {impersonateLoading ? '...' : 'Ir'}
        </button>
      </div>
    </div>
  );

  if (!user) {
    const isLandingRoute = location.pathname === '/';
    return (
      <nav className={styles.nav}>
        <div className={styles.navInner}>
          <div className={styles.desktopLeft}>
            <div
              className={styles.brand}
              onClick={() => navigate('/')}
              onKeyDown={(e) => e.key === 'Enter' && navigate('/')}
              role="button"
              tabIndex={0}
            >
              <LogoImage size={64} alt="Athena Simulados" className={styles.logo} />
              <span className={styles.brandTextGroup}>
                <span className={styles.brandName}>Athena Simulados</span>
              </span>
            </div>
            {isLandingRoute && (
              <div className={styles.navLinks}>
                {PUBLIC_NAV_ANCHORS.map((item) => (
                  <a key={item.href} href={item.href} className={styles.navLink}>
                    {item.label}
                  </a>
                ))}
              </div>
            )}
          </div>
          <div className={styles.actions}>
            <button
              type="button"
              onClick={toggleTheme}
              title={
                preferences.theme === 'light'
                  ? 'Alternar para tema escuro'
                  : 'Alternar para tema claro'
              }
              className={styles.themeBtn}
              aria-label={
                preferences.theme === 'light'
                  ? 'Alternar para tema escuro'
                  : 'Alternar para tema claro'
              }
            >
              <ThemeToggleIcon currentTheme={preferences.theme} />
            </button>
            <button type="button" onClick={() => navigate('/login')} className={styles.ghostBtn}>
              Entrar
            </button>
            <button type="button" onClick={() => navigate('/catalogo')} className={styles.loginBtn}>
              Começar grátis
            </button>
          </div>
        </div>
      </nav>
    );
  }

  return (
    <nav className={styles.nav}>
      <div className={styles.navInner}>
        <div className={styles.desktopLeft}>
          <div
            className={styles.brand}
            onClick={() => handleNav('/dashboard')}
            onKeyDown={(e) => e.key === 'Enter' && handleNav('/dashboard')}
            role="button"
            tabIndex={0}
          >
            <LogoImage size={64} alt="Athena Simulados" className={styles.logo} />
            <span className={styles.brandTextGroup}>
              <span className={styles.brandName}>Athena Simulados</span>
            </span>
          </div>
          {/* <div className={styles.modeSelector} role="group" aria-label="Modo do aplicativo">
            {MODE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`${styles.modeBtn} ${preferences.appMode === opt.value ? styles.modeBtnActive : ''}`}
                onClick={() => handleModeChange(opt.value)}
                aria-pressed={preferences.appMode === opt.value}
              >
                {opt.label}
              </button>
            ))}
          </div> */}
          <div className={styles.navLinks}>
            {navItems.map((item) => {
              const active = item.action ? false : isActive(item.path);
              return (
                <button
                  key={item.path}
                  type="button"
                  onClick={() => handleNavItem(item)}
                  className={`${styles.navLink} ${active ? styles.navLinkActive : ''}`}
                  aria-current={active ? 'page' : undefined}
                >
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className={styles.actions}>
          {shouldShowCreditsBadge(accountType) && (
            <div className={styles.creditsBadgeSlot}>
              <CreditsBadge variant="compact" exclude={['correction']} />
            </div>
          )}
          <button
            type="button"
            onClick={toggleTheme}
            title={
              preferences.theme === 'light'
                ? 'Alternar para tema escuro'
                : 'Alternar para tema claro'
            }
            className={styles.themeBtn}
            aria-label={
              preferences.theme === 'light'
                ? 'Alternar para tema escuro'
                : 'Alternar para tema claro'
            }
          >
            <ThemeToggleIcon currentTheme={preferences.theme} />
          </button>

          <button
            type="button"
            className={styles.hamburger}
            onClick={() => setMobileMenuOpen(true)}
            aria-expanded={mobileMenuOpen}
            aria-label="Abrir menu"
          >
            <Menu size={20} aria-hidden />
          </button>

          <div className={styles.userDropdown}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className={styles.userTrigger} aria-label="Menu do usuário">
                  {userData?.profile?.photoUrl ? (
                    <img src={userData.profile.photoUrl} alt="" className={styles.avatar} />
                  ) : (
                    <div className={styles.avatarFallback}>
                      {userData?.profile?.name?.[0]?.toUpperCase() ||
                        user?.email?.[0]?.toUpperCase() ||
                        'U'}
                    </div>
                  )}
                  <div className={styles.userInfo}>
                    <span className={styles.userName}>
                      {userData?.profile?.name || user?.email || 'Usuário'}
                    </span>
                    {userData?.roles?.admin && userData?.entitlements?.subscriptionStatus && (
                      <span
                        className={`${styles.statusBadge} ${getStatusClass(userData.entitlements.subscriptionStatus)}`}
                      >
                        {userData.entitlements.subscriptionStatus === 'active'
                          ? 'Ativo'
                          : 'Sem Assinatura'}
                      </span>
                    )}
                  </div>
                  <ChevronDown size={18} aria-hidden />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className={styles.dropdown}>
                <div className={styles.dropdownHeader}>
                  <div className={styles.dropdownName}>
                    {userData?.profile?.name || user?.email}
                  </div>
                  <div className={styles.dropdownEmail}>{user?.email}</div>
                </div>
                <div className={styles.dropdownContent}>
                  <DropdownMenuItem
                    className={styles.dropdownItem}
                    onSelect={() => navigate('/conta')}
                  >
                    <User size={18} aria-hidden />
                    Minha Conta
                  </DropdownMenuItem>
                  {userData?.roles?.admin && (
                    <DropdownMenuItem
                      className={styles.dropdownItem}
                      onSelect={() => navigate('/assinatura')}
                    >
                      Assinatura
                    </DropdownMenuItem>
                  )}
                  {userData?.roles?.admin && !isImpersonating && renderImpersonateSection()}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className={`${styles.dropdownItem} ${styles.logoutItem}`}
                    onSelect={handleLogout}
                  >
                    <LogOut size={18} aria-hidden />
                    Sair
                  </DropdownMenuItem>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <NavDrawer
        open={mobileMenuOpen}
        onClose={closeMobileMenu}
        user={{
          name: userData?.profile?.name || '',
          email: user?.email || '',
          photoUrl: userData?.profile?.photoUrl ?? undefined,
        }}
        modeOptions={MODE_OPTIONS}
        appMode={preferences.appMode}
        onModeChange={handleModeChange}
        navItems={navItems}
        isActive={isActive}
        onNavItem={handleNavItem}
        onLogout={handleLogout}
        adminExtras={
          userData?.roles?.admin && !isImpersonating ? renderImpersonateSection() : undefined
        }
      />
    </nav>
  );
}
